import React from 'react';
import { Smartphone } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { AppleIcon, ICON_DISPLAY } from './constants';


/* ── Stamp overlay drawn ON the banner ──
   Sized relative to the banner width (like the real pass strip):
   white circles, filled = opaque with the stamp icon, empty = translucent. */
const StampOverlay = ({ formData, exampleFilled = 3 }) => {
  const total = formData.total_stamps || 10;
  const icon = formData.stamp_icon || '';
  const iconUrl = icon.startsWith('http') ? icon : null;
  const cols = Math.min(5, total);
  const rows = Math.ceil(total / cols);
  const size = total <= 6 ? 13 : total <= 10 ? 11 : 8.5; // % of banner width
  const gap = 2.6;

  return (
    <div className="absolute inset-0 flex flex-col items-center justify-center gap-1.5 pointer-events-none" data-testid="strip-stamp-grid">
      {Array.from({ length: rows }, (_, row) => {
        const rowStart = row * cols;
        const rowCount = Math.min(cols, total - rowStart);
        return (
          <div key={row} className="w-full flex justify-center" style={{ columnGap: `${gap}%` }}>
            {Array.from({ length: rowCount }, (_, col) => {
              const idx = rowStart + col;
              const isFilled = idx < exampleFilled;
              return (
                <div
                  key={idx}
                  className="rounded-full flex items-center justify-center shrink-0"
                  style={{
                    // % of the banner width: works on every browser (no container units needed).
                    width: `${size}%`, aspectRatio: '1 / 1',
                    backgroundColor: isFilled ? '#FFFFFF' : 'rgba(255,255,255,0.6)',
                  }}
                  data-testid={`strip-stamp-${idx}`}
                >
                  {isFilled && (iconUrl
                    ? <img src={iconUrl} alt="" style={{ width: '70%', height: '70%', objectFit: 'contain' }} />
                    : <span style={{ fontSize: `${size * 0.55}cqw`, lineHeight: 1 }}>{ICON_DISPLAY[icon] || (icon && icon.length <= 4 ? icon : '✓')}</span>)}
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
};

export const AppleWalletPreview = ({ formData, getLogoUrl, variant = 'compact', memberName = 'Léa Martin', qrValue }) => {
  const isFull = variant === 'full';
  const isStamps = formData.card_type === 'stamps';

  return (
    <div className="max-w-[400px] mx-auto">
      <div
        className="rounded-2xl overflow-hidden shadow-2xl"
        style={{ backgroundColor: formData.primary_color }}
        data-testid="apple-wallet-preview"
      >
        {/* Header bar */}
        <div className="flex items-center justify-between px-4 py-3" style={{ color: formData.secondary_color }}>
          <div className="flex items-center gap-2 min-w-0">
            {formData.logo_url ? (
              <img src={getLogoUrl(formData.logo_url)} alt="Logo" className="w-10 h-10 rounded-lg object-cover flex-shrink-0" />
            ) : (
              <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center flex-shrink-0">
                <span className="text-sm font-bold">{formData.name?.charAt(0) || 'L'}</span>
              </div>
            )}
            <span className="font-semibold text-sm truncate">{formData.name || 'Nom de la carte'}</span>
          </div>
          <div className="text-right flex-shrink-0 ml-2">
            <p className={`text-[10px] uppercase opacity-70${isFull ? ' tracking-wide' : ''}`}>
              {isStamps ? 'TAMPONS' : 'POINTS'}
            </p>
            <p className="text-lg font-bold leading-tight">
              {isStamps ? `${Math.min(3, formData.total_stamps || 10)}/${formData.total_stamps || 10}` : '0'}
            </p>
          </div>
        </div>

        {/* Strip image zone — stamps overlay ON the banner */}
        <div className="relative w-full" style={{ aspectRatio: '1125/432', containerType: 'inline-size' }}>
          {formData.strip_url ? (
            <img src={getLogoUrl(formData.strip_url)} alt="Strip" className="w-full h-full object-cover" />
          ) : formData.background_url ? (
            <img src={getLogoUrl(formData.background_url)} alt="Bg" className="w-full h-full object-cover" />
          ) : (
            <div
              className="w-full h-full flex flex-col items-center justify-start pt-4"
              style={{ background: `linear-gradient(180deg, ${formData.primary_color} 0%, ${formData.primary_color}88 100%)` }}
            >
              {!isStamps && (
                <span className="text-sm font-bold opacity-30" style={{ color: formData.secondary_color }}>
                  {formData.name || 'BANNIÈRE'}
                </span>
              )}
            </div>
          )}
          {/* Stamp circles drawn as overlay on banner */}
          {isStamps && <StampOverlay formData={formData} />}
        </div>

        {/* Fields below strip */}
        <div className={`px-4 py-3${isFull ? ' space-y-2' : ''}`} style={{ color: formData.secondary_color }}>
          <div className={`flex justify-between${isFull ? '' : ' mb-1'}`}>
            <div>
              <p className={`text-[10px] uppercase opacity-60${isFull ? ' tracking-wide' : ''}`}>MEMBRE</p>
              <p className="text-sm font-medium">{memberName}</p>
            </div>
            <div className="text-right">
              <p className={`text-[10px] uppercase opacity-60${isFull ? ' tracking-wide' : ''}`}>RÉCOMPENSE</p>
              <p className={`text-sm${isFull ? ' font-medium' : ''}`}>
                {isStamps ? `${formData.total_stamps || 10} tampons` : `à ${formData.reward_threshold} pts`}
              </p>
            </div>
          </div>
          <div>
            <p className={`text-[10px] uppercase opacity-60${isFull ? ' tracking-wide' : ''}`}>DEPUIS</p>
            <p className="text-xs opacity-70">{new Date().toLocaleDateString('fr-FR')}</p>
          </div>
        </div>

        {/* QR Code area (full variant only) */}
        {isFull && (
          <div className="bg-white mx-4 mb-4 rounded-xl p-4 flex flex-col items-center">
            <QRCodeSVG value={qrValue || `${window.location.origin}/s/LP-000000`} size={128} level="M" />
            <p className="text-[10px] text-slate-400 mt-2">LP-000000</p>
          </div>
        )}
      </div>

      {/* Wallet badges (full variant only) */}
      {isFull && (
        <div className="flex items-center justify-center gap-4 mt-4">
          <div className="flex items-center gap-1 text-xs text-slate-500">
            <AppleIcon className="w-4 h-4" /> Apple Wallet
          </div>
          <div className="flex items-center gap-1 text-xs text-slate-500">
            <Smartphone className="w-4 h-4" /> Google Wallet
          </div>
        </div>
      )}
    </div>
  );
};
